import { useCallback, useRef, useState } from 'react';
import { downloadOkruPart } from '@/lib/download/downloadOkruPart';

export type OkruDownloadState =
  | { kind: 'idle' }
  | { kind: 'downloading' }
  | { kind: 'done' }
  | { kind: 'canceled' }
  | { kind: 'error'; message: string };

export interface UseOkruDownloadResult {
  state: OkruDownloadState;
  start(part: number, filename: string): Promise<void>;
  cancel(): void;
  reset(): void;
}

export function useOkruDownload(titleId: string): UseOkruDownloadResult {
  const [state, setState] = useState<OkruDownloadState>({ kind: 'idle' });
  const ctrlRef = useRef<AbortController | null>(null);

  const start = useCallback(async (part: number, filename: string): Promise<void> => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setState({ kind: 'downloading' });
    try {
      await downloadOkruPart(titleId, part, filename, ctrl.signal, {});
      if (!ctrl.signal.aborted) setState({ kind: 'done' });
    } catch (err) {
      if (ctrl.signal.aborted || (err as Error)?.name === 'AbortError') setState({ kind: 'canceled' });
      else setState({ kind: 'error', message: (err as Error)?.message ?? 'download failed' });
    }
  }, [titleId]);

  const cancel = useCallback((): void => { ctrlRef.current?.abort(); }, []);
  const reset = useCallback((): void => { setState({ kind: 'idle' }); }, []);

  return { state, start, cancel, reset };
}
